import React, { useState, useEffect } from 'react';
import { Table, Card, Typography, Button, Space, message, Spin, Tag, Input, Row, Col } from 'antd';
import { PlusOutlined, ReloadOutlined, EditOutlined, FormOutlined, SearchOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { getMyProjects, Project } from '../../services/api';
import './styles.css';

const { Title } = Typography;
const { Search } = Input;

interface ExDataItem {
  type: string;
  is_ex: boolean | number;
  label: string;
  model: string;
  placeholder: string;
}

const Projects: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [filteredProjects, setFilteredProjects] = useState<Project[]>([]);
  const [searchValue, setSearchValue] = useState('');
  const navigate = useNavigate();

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await getMyProjects();
      if (response.data.code === 0 && response.data.data) {
        setProjects(response.data.data);
        setFilteredProjects(response.data.data);
      } else {
        message.error(response.data.msg || '获取授权协议列表失败');
      }
    } catch (error) {
      console.error('获取授权协议列表错误:', error);
      message.error('获取授权协议列表失败，请检查网络连接后重试');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Filter projects based on search value
  useEffect(() => {
    if (!searchValue) {
      setFilteredProjects(projects);
      return;
    }
    
    const searchTerm = searchValue.toString().trim().toLowerCase();
    
    const filtered = projects.filter(project => 
      // Search by protocol ID (convert to string for comparison)
      String(project.id).toLowerCase().includes(searchTerm) || 
      // Search by protocol name/title
      (project.title && project.title.toLowerCase().includes(searchTerm))
    );
    
    setFilteredProjects(filtered);
  }, [projects, searchValue]);

  const handleSearch = (value: string) => {
    setSearchValue(value);
  };

  const handleResetSearch = () => {
    setSearchValue('');
  };

  // Handle input change to ensure numeric values are properly processed
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const getStatusText = (status: number) => {
    switch (status) {
      case 0:
        return { text: '禁用', color: 'red' };
      case 1:
        return { text: '启用', color: 'green' };
      default:
        return { text: '未知', color: 'gray' };
    }
  };
  const columns = [
    {
      title: '协议ID',
      dataIndex: 'id',
      key: 'id',
      width: 80,
    },
    {
      title: '协议名称',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: Project) => (
        <a onClick={() => navigate(`/protocol-projects/${record.id}`, { state: { protocolTitle: record.name } })}>{text}</a>
      ),
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: number) => {
        const { text, color } = getStatusText(status);
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '创建时间',
      dataIndex: 'addTime',
      key: 'addTime',
      width: 180,
      render: (text: string) => new Date(text).toLocaleString(),
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_: any, record: Project) => (
        <Space size="middle">
          <a onClick={() => navigate(`/protocol-projects/${record.id}`, { state: { protocolTitle: record.title } })}>查看</a>
          <a onClick={() => navigate(`/project-edit/${record.id}`)}>编辑</a>
        </Space>
      ),
    },
  ];

  return (
    <div className="projects-container">
      <Card className="projects-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Title level={2}>授权协议列表</Title>
          <Space>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => navigate('/project-create')}
            >
              新建授权协议
            </Button>
            <Button 
              icon={<ReloadOutlined />}
              onClick={fetchProjects}
            >
              刷新
            </Button>
          </Space>
        </div>
        
        {/* Search Section */}
        <div style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8} lg={6}>
              <Search
                placeholder="搜索协议ID或协议名称"
                allowClear
                enterButton={<SearchOutlined />}
                value={searchValue}
                onChange={handleInputChange}
                onSearch={handleSearch}
              />
            </Col>
            {searchValue && (
              <Col>
                <Button onClick={handleResetSearch}>重置搜索</Button>
              </Col>
            )}
          </Row>
        </div>
        
        <Spin spinning={loading}>
          <Table 
            dataSource={filteredProjects} 
            columns={columns} 
            rowKey="id"
            pagination={{
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total) => `共 ${total} 条记录`,
              pageSize: 10,
            }}
          />
        </Spin>
      </Card>
    </div>
  );
};

export default Projects; 